import { Component, DestroyRef, EventEmitter, inject, Injector, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NgControl,
  NgModel,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { InputItemBaseModel, InputValueType } from '@libs/form/models';
import { PARENT_NG_CONTROL } from './dynamic-elements.model';
import { ItemBaseComponent } from './item-base.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export abstract class InputBaseComponent<
  TModel extends InputItemBaseModel = InputItemBaseModel,
  TValue extends TModel['value'] = InputValueType,
> extends ItemBaseComponent<TModel>
  implements ControlValueAccessor, Validator, OnInit, OnChanges {

  private static objectCount: number = 0;

  private readonly injector: Injector = InputBaseComponent.getInjector();

  protected emptyValueMessage: string = 'keine Angabe';
  protected onTouchedCallback: () => void = this.onTouchedDefaultCallback.bind(this);
  protected onChangeCallback: (value: TValue) => void = this.onChangeDefaultCallback.bind(this);

  protected readonly destroyRef: DestroyRef = inject(DestroyRef);

  @Output()
  public readonly valueChanges: EventEmitter<TValue> = new EventEmitter<TValue>();

  public id!: string;
  public control!: FormControl;

  public abstract valueAccessor: ControlValueAccessor;

  public override get classList(): string[] {
    const hasSubscript: boolean = !!this.data?.hint || !!this.control?.errors;
    const hasDynamicSubscript: boolean = this.data.subscriptSizing === 'dynamic' || hasSubscript;

    return [
      'form-input',
      ...super.classList,
      !hasDynamicSubscript ? 'fixed-subscript' : '',
    ];
  }

  public static getInjector(): Injector {
    return inject(Injector);
  }

  public static resetCounter(): void {
    InputBaseComponent.objectCount = 0;
  }

  public ngOnInit(): void {
    this.initId();
    this.initControl();
    this.initValue();
    this.syncDisabledState();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || !this.control) {
      return;
    }

    this.syncDisabledState();

    if (this.data.value !== undefined) {
      this.control.setValue(this.data.value);
    }
  }

  public writeValue(value: unknown): void {
    this.valueAccessor.writeValue(value);
  }

  public registerOnChange(fn: (_: unknown) => unknown): void {
    this.valueAccessor.registerOnChange(fn);
  }

  public registerOnTouched(fn: () => void): void {
    this.valueAccessor.registerOnTouched(fn);
  }

  public setDisabledState(isDisabled: boolean): void {
    this.valueAccessor.setDisabledState?.(isDisabled);
  }

  public validate(_control: AbstractControl): ValidationErrors | null {
    return null;
  }

  protected initId(): void {
    this.id ??= `${this.data.name}-${++InputBaseComponent.objectCount}`;
  }

  protected initControl(): void {
    // Do nothing if already initialized
    if (this.control) {
      return;
    }

    // Check if we have NgControl (NgModel, FormControl and FormControlName directives)
    const ngControl: NgControl | null =
      this.injector.get(NgControl, null, { self: true })
      ?? this.injector.get(PARENT_NG_CONTROL, null);

    // NgModel and FormControl directives come with initialized control
    if (ngControl instanceof NgModel || ngControl instanceof FormControlDirective) {
      this.control = ngControl.control;

      return;
    }

    // FormControlName directive requires FromGroupDirective which contains control
    if (ngControl instanceof FormControlName) {
      const ngFormGroup: FormGroupDirective = this.injector.get(FormGroupDirective);

      this.control = ngFormGroup.getControl(ngControl);

      return;
    }

    // Create default control if needed
    this.control = new FormControl();
  }

  protected initValue(): void {
    if (this.data.value !== undefined) {
      this.control.setValue(this.data.value, { emitEvent: false });
    }

    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => void this.valueChanges.emit(value as TValue));
  }

  private syncDisabledState(): void {
    if (this.data.attributes?.disabled && !this.control.disabled) {
      this.control.disable();
    } else if (!this.control.parent?.disabled && this.control.disabled) {
      this.control.enable();
    }
  }

  private onChangeDefaultCallback(value: InputValueType): void {
    this.control.setValue(value);
    this.control.markAsDirty();
  }

  private onTouchedDefaultCallback(): void {
    this.control.markAsTouched();
  }
}
